import React, { useEffect, useState } from 'react'
import { styled } from '@mui/material/styles'
import { safeJsonParse } from '../utils/jsonUtils'

import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import CssBaseline from '@mui/material/CssBaseline'
import MuiAppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import List from '@mui/material/List'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import Badge from '@mui/material/Badge'

import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import DashboardIcon from '@mui/icons-material/Dashboard'
import PersonIcon from '@mui/icons-material/Person'
import ArchiveIcon from '@mui/icons-material/Archive'
import AssignmentReturnedIcon from '@mui/icons-material/AssignmentReturned'
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter'
import SettingsIcon from '@mui/icons-material/Settings'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined'
import MemoryIcon from '@mui/icons-material/Memory'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import CloudUpload from '@mui/icons-material/CloudUpload'
import CreditCardIcon from '@mui/icons-material/CreditCard'

import {
   Collapse,
   Menu,
   MenuItem,
   Avatar,
} from '@mui/material'

import {
   useNavigate,
   useLocation,
   Outlet,
} from 'react-router-dom'

import LogoIcon2 from '../assets/img/blue-logo.png'
import KRIcon from '../assets/img/gray-kr.svg'

import axios from '../api/axios'
import socket from '../socket'

const expandedWidth = 232
const collapsedWidth = 68
const headerHeight = 60

const Main = styled('main')(({ theme }) => ({
   flexGrow: 1,
   minWidth: 0,
   width: 'auto',
   height: '100vh',
   marginLeft: 0,

   overflowX: 'hidden',
   overflowY: 'auto',

   transition: theme.transitions.create(
      ['width'],
      {
         easing: theme.transitions.easing.easeOut,
         duration: 180,
      }
   ),

   scrollbarGutter: 'stable',
}))

const AppBar = styled(MuiAppBar, {
   shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => {
   const width = open ? expandedWidth : collapsedWidth

   return {
      width: `calc(100% - ${width}px)`,
      marginLeft: `${width}px`,
      backgroundColor: theme.palette.primary.main,
      backgroundImage: 'none',
      boxShadow: '0 1px 0 rgba(16, 24, 40, 0.10)',
      transition: theme.transitions.create(
         ['margin-left', 'width'],
         {
            easing: theme.transitions.easing.easeOut,
            duration: 180,
         }
      ),
   }
})

export default function AdminSidebar() {
   const [open, setOpen] = useState(true)

   const [logo, setLogo] = useState('')
   const [account, setAccount] = useState(null)
   const [avatar, setAvatar] = useState('')

   const [unreadCount1, setUnreadCount1] = useState(0)
   const [unreadCount2, setUnreadCount2] = useState(0)
   const [newUserCount, setNewUserCount] = useState(0)

   const [anchorElUser, setAnchorElUser] = useState(null)

   const [listopen, setListOpen] = useState(true)
   const [creditopen, setCreditOpen] = useState(true)

   const navigate = useNavigate()
   const location = useLocation()

   const pageTitles = {
      '/admin_dashboard': 'Dashboard',
      '/admin_user': 'Users',
      '/admin_upload': 'File System',
      '/admin_service': 'Services',
      '/admin_creditlist': 'Credit List',
      '/admin_pricelist': 'Price List',
      '/admin_toolbox': 'Toolbox',
      '/admin_cuw_decoder': 'CUW Decoder',
      '/admin_profilsetting': 'Settings',
      '/admin_invoice': 'Invoices',
      '/admin_support': 'Support',
      '/admin_archive': 'Archive',
   }

   const pageTitle =
      pageTitles[location.pathname] || 'FILE HUB'

   const getNewUserCount = async () => {
      try {
         const result = await axios.post('getUserData')

         if (result.status === 200 && Array.isArray(result.data)) {
            setNewUserCount(
               result.data.filter(
                  (user) => user.registrationReviewed === false
               ).length
            )
         }
      } catch (error) {
         if (process.env.REACT_APP_MODE) {
            console.log(error)
         }
      }
   }

   const getUserUnreadCount = async (id) => {
      try {
         const result = await axios.post(
            'getUserUnreadCount',
            { id }
         )

         if (result.data.status) {
            setUnreadCount1(
               result.data.supportUnreadCount
            )

            setUnreadCount2(
               result.data.archiveUnreadCount
            )
         }
      } catch (error) {
         if (process.env.REACT_APP_MODE) {
            console.log(error)
         }
      }
   }

   const getLogo = async () => {
      try {
         const result = await axios.post('getLogo')

         if (result.data.status) {
            setLogo(
               process.env.REACT_APP_BASE_URL +
                  result.data.data
            )
         }
      } catch (error) {
         if (process.env.REACT_APP_MODE) {
            console.log(error)
         }
      }
   }

   useEffect(() => {
      const user = localStorage.getItem('user')

      if (user) {
         const parsed = safeJsonParse(user)
         setAccount(parsed)
      }
   }, [])

   useEffect(() => {
      if (!account) return

      getLogo()
      setAvatar(account?.profile)
      getNewUserCount()

      if (account?._id) {
         getUserUnreadCount(account._id)
      }

      const handleNewUserRegistration = async () => {
         await getNewUserCount()
      }

      const handleUserRegistrationChanged = async () => {
         await getNewUserCount()
      }

      const handleAccountMessage = async (e) => {
         if (e.support) {
            setUnreadCount1((prev) => prev + 1)
         } else {
            setUnreadCount2((prev) => prev + 1)
         }
      }

      socket.on(account?._id, handleAccountMessage)
      socket.on('newUserRegistration', handleNewUserRegistration)
      socket.on('userRegistrationChanged', handleUserRegistrationChanged)

      socket.on(
         'checkUnreadCount' + account?._id,
         async () => {
            await getUserUnreadCount(account?._id)
         }
      )

      socket.on('sendToChatBox', async () => {
         await getUserUnreadCount(account?._id)
      })

      socket.on('sendToArchive', async () => {
         await getUserUnreadCount(account?._id)
      })

      return () => {
         socket.off(account?._id, handleAccountMessage)
         socket.off('newUserRegistration', handleNewUserRegistration)
         socket.off('userRegistrationChanged', handleUserRegistrationChanged)
         socket.off('sendToArchive')
         socket.off('sendToChatBox')
         socket.off(
            'checkUnreadCount' + account?._id
         )
      }
   }, [account])

   const handleLogout = () => {
      localStorage.clear()
      window.location.replace(`${window.origin}/`)
   }

   const isActive = (...paths) =>
      paths.includes(location.pathname)

   const NavItem = ({
      label,
      icon,
      onClick,
      active = false,
      nested = false,
      count = 0,
   }) => {
      const displayedIcon =
         !open && count > 0 ? (
            <Badge
               badgeContent={count}
               color="secondary"
               max={99}
            >
               {icon}
            </Badge>
         ) : (
            icon
         )

      return (
         <Tooltip
            title={!open ? label : ''}
            placement="right"
            arrow
         >
            <ListItem
               disablePadding
               sx={{
                  display: 'block',
                  mb: '3px',
               }}
            >
               <ListItemButton
                  onClick={onClick}
                  selected={active}
                  sx={{
                     minHeight: nested ? 40 : 44,
                     borderRadius: '8px',

                     justifyContent: open
                        ? 'initial'
                        : 'center',

                     pl:
                        open && nested
                           ? 4
                           : open
                           ? 1.5
                           : 1,

                     pr: open ? 1.5 : 1,

                     color: active
                        ? 'primary.main'
                        : '#344054',

                     '& .MuiListItemIcon-root': {
                        color: active
                           ? 'primary.main'
                           : '#7A8491',
                     },

                     '&.Mui-selected': {
                        backgroundColor:
                           'rgba(47, 128, 237, 0.10)',
                     },

                     '&.Mui-selected:hover': {
                        backgroundColor:
                           'rgba(47, 128, 237, 0.15)',
                     },

                     '&:hover': {
                        backgroundColor: '#F4F7FA',
                     },
                  }}
               >
                  <ListItemIcon
                     sx={{
                        minWidth: 0,
                        mr: open ? 1.5 : 0,
                        justifyContent: 'center',
                     }}
                  >
                     {displayedIcon}
                  </ListItemIcon>

                  {open && (
                     <>
                        <ListItemText
                           primary={label}
                           primaryTypographyProps={{
                              fontSize: nested
                                 ? 14
                                 : 15,
                              fontWeight: active
                                 ? 600
                                 : 500,
                              whiteSpace: 'nowrap',
                           }}
                        />

                        {count > 0 && (
                           <Box
                              sx={{
                                 ml: 1,
                                 minWidth: 24,
                                 height: 22,
                                 px: 0.75,
                                 borderRadius: '11px',
                                 display: 'flex',
                                 alignItems: 'center',
                                 justifyContent:
                                    'center',
                                 backgroundColor:
                                    'secondary.main',
                                 color: '#fff',
                                 fontSize: 12,
                                 fontWeight: 700,
                              }}
                           >
                              {count}
                           </Box>
                        )}
                     </>
                  )}
               </ListItemButton>
            </ListItem>
         </Tooltip>
      )
   }

   return (
      <Box
         sx={{
            display: 'flex',
            minHeight: '100vh',
            backgroundColor: 'background.default',
         }}
      >
         <CssBaseline />

         <AppBar
            position="fixed"
            open={open}
            elevation={0}
         >
            <Toolbar
               sx={{
                  minHeight:
                     `${headerHeight}px !important`,
                  px: { xs: 2, md: 3 },
               }}
            >
               <Typography
                  variant="h6"
                  noWrap
                  component="div"
                  sx={{
                     fontSize: '20px',
                     fontWeight: 500,
                     letterSpacing: '-0.2px',
                  }}
               >
                  {pageTitle}
               </Typography>

               <Box sx={{ flexGrow: 1 }} />

               <Typography
                  sx={{
                     mr: 1.5,
                     fontSize: '15px',
                     fontWeight: 500,
                     display: {
                        xs: 'none',
                        sm: 'block',
                     },
                  }}
               >
                  Admin
               </Typography>

               <Avatar
                  onClick={(event) =>
                     setAnchorElUser(
                        event.currentTarget
                     )
                  }
                  sx={{
                     width: 40,
                     height: 40,
                     cursor: 'pointer',
                     bgcolor:
                        'rgba(0,169,206,0.18)',
                     border:
                        '1px solid rgba(0,169,206,0.65)',
                  }}
               >
                  <PersonIcon sx={{ fontSize: '22px', color: '#FFFFFF' }} />
               </Avatar>

               <Menu
                  anchorEl={anchorElUser}
                  open={Boolean(anchorElUser)}
                  onClose={() =>
                     setAnchorElUser(null)
                  }
                  anchorOrigin={{
                     vertical: 'bottom',
                     horizontal: 'right',
                  }}
                  transformOrigin={{
                     vertical: 'top',
                     horizontal: 'right',
                  }}
               >
                  <MenuItem onClick={handleLogout}>
                     Log out
                  </MenuItem>
               </Menu>
            </Toolbar>
         </AppBar>

         <Drawer
            variant="permanent"
            sx={{
               width: open
                  ? expandedWidth
                  : collapsedWidth,

               flexShrink: 0,
               whiteSpace: 'nowrap',

               '& .MuiDrawer-paper': {
                  width: open
                     ? expandedWidth
                     : collapsedWidth,

                  overflowX: 'hidden',
                  boxSizing: 'border-box',
                  backgroundColor: '#FFFFFF',
                  borderRight:
                     '1px solid #E4E7EC',

                  transition:
                     'width 180ms ease',
               },
            }}
         >
            <Box
               sx={{
                  height: `${headerHeight}px`,
                  minHeight: `${headerHeight}px`,
                  display: 'flex',
                  alignItems: 'center',

                  justifyContent: open
                     ? 'space-between'
                     : 'center',

                  px: open ? 1.5 : 0.75,
               }}
            >
               {open && (
                  <div
                        style={{
                           display: 'flex',
                           alignItems: 'center',
                           minWidth: 0,
                        }}
                     >
                        <div
                           style={{
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              lineHeight: 1,
                           }}
                        >
                           <div
                              style={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: '7px',
                                 whiteSpace: 'nowrap',
                              }}
                           >
                              <span
                                 style={{
                                    fontSize: open ? '20px' : '17px',
                                    fontWeight: 700,
                                    letterSpacing: '0.2px',
                                    color: open
                                       ? '#163A5F'
                                       : '#FFFFFF',
                                 }}
                              >
                                 FILE HUB
                              </span>

                              <span
                                 style={{
                                    padding: '3px 8px',
                                    borderRadius: '999px',
                                    backgroundColor: '#00A9CE',
                                    color: '#FFFFFF',
                                    fontSize: '10px',
                                    lineHeight: 1,
                                    fontWeight: 800,
                                    letterSpacing: '0.5px',
                                    boxShadow:
                                       '0 2px 6px rgba(0,169,206,0.25)',
                                 }}
                              >
                                 V2
                              </span>
                           </div>

                           {open && (
                              <span
                                 style={{
                                    marginTop: '5px',
                                    fontSize: '9px',
                                    fontWeight: 700,
                                    letterSpacing: '1.1px',
                                    color: '#7A8699',
                                    whiteSpace: 'nowrap',
                                 }}
                              >
                                 BY ZIPTUNING
                              </span>
                           )}
                        </div>
                     </div>
               )}

               <IconButton
                  onClick={() => setOpen(!open)}
                  size="small"
                  sx={{
                     width: 36,
                     height: 36,
                     color: '#667085',
                  }}
               >
                  {open ? (
                     <ChevronLeftIcon />
                  ) : (
                     <MenuIcon />
                  )}
               </IconButton>
            </Box>

            <Divider />

            <List
               sx={{
                  px: open ? 1.25 : 0.75,
                  py: 1.25,
               }}
            >
               <NavItem
                  label="Dashboard"
                  icon={<DashboardIcon />}
                  active={isActive(
                     '/admin_dashboard'
                  )}
                  onClick={() =>
                     navigate('/admin_dashboard')
                  }
               />

               <NavItem
                  label="Users"
                  icon={<PersonIcon />}
                  count={newUserCount}
                  active={isActive('/admin_user')}
                  onClick={() =>
                     navigate('/admin_user')
                  }
               />

               <NavItem
                  label="File System"
                  icon={
                     <AssignmentReturnedIcon />
                  }
                  active={false}
                  onClick={() => {
                     if (open) {
                        setListOpen(!listopen)
                     } else {
                        navigate('/admin_upload')
                     }
                  }}
               />

               {open ? (
                  <Collapse
                     in={listopen}
                     timeout="auto"
                  >
                     <NavItem
                        nested
                        label="Upload"
                        icon={<CloudUpload />}
                        active={isActive(
                           '/admin_upload'
                        )}
                        onClick={() =>
                           navigate(
                              '/admin_upload'
                           )
                        }
                     />
                  </Collapse>
               ) : (
                  <NavItem
                     label="Upload"
                     icon={<CloudUpload />}
                     active={isActive(
                        '/admin_upload'
                     )}
                     onClick={() =>
                        navigate('/admin_upload')
                     }
                  />
               )}

               <NavItem
                  label="Services"
                  icon={
                     <BusinessCenterIcon />
                  }
                  active={isActive(
                     '/admin_service'
                  )}
                  onClick={() =>
                     navigate('/admin_service')
                  }
               />

               <NavItem
                  label="Credit"
                  icon={
                     <AssignmentReturnedIcon />
                  }
                  active={false}
                  onClick={() => {
                     if (open) {
                        setCreditOpen(
                           !creditopen
                        )
                     } else {
                        navigate(
                           '/admin_creditlist'
                        )
                     }
                  }}
               />

               {open ? (
                  <Collapse
                     in={creditopen}
                     timeout="auto"
                  >
                     <NavItem
                        nested
                        label="Credit List"
                        icon={<CreditCardIcon />}
                        active={isActive(
                           '/admin_creditlist'
                        )}
                        onClick={() =>
                           navigate(
                              '/admin_creditlist'
                           )
                        }
                     />

                     <NavItem
                        nested
                        label="Price List"
                        icon={
                           <img
                              src={KRIcon}
                              alt="kr"
                              style={{
                                 width: 24,
                                 height: 24,
                              }}
                           />
                        }
                        active={isActive(
                           '/admin_pricelist'
                        )}
                        onClick={() =>
                           navigate(
                              '/admin_pricelist'
                           )
                        }
                     />
                  </Collapse>
               ) : (
                  <>
                     <NavItem
                        label="Credit List"
                        icon={<CreditCardIcon />}
                        active={isActive(
                           '/admin_creditlist'
                        )}
                        onClick={() =>
                           navigate(
                              '/admin_creditlist'
                           )
                        }
                     />

                     <NavItem
                        label="Price List"
                        icon={
                           <img
                              src={KRIcon}
                              alt="kr"
                              style={{
                                 width: 24,
                                 height: 24,
                              }}
                           />
                        }
                        active={isActive(
                           '/admin_pricelist'
                        )}
                        onClick={() =>
                           navigate(
                              '/admin_pricelist'
                           )
                        }
                     />
                  </>
               )}

               <NavItem
                  label="Toolbox"
                  icon={<BuildCircleOutlinedIcon />}
                  active={isActive('/admin_toolbox')}
                  onClick={() =>
                     navigate('/admin_toolbox')
                  }
               />

               <NavItem
                  label="CUW Decoder"
                  icon={<MemoryIcon />}
                  active={isActive('/admin_cuw_decoder')}
                  onClick={() =>
                     navigate('/admin_cuw_decoder')
                  }
               />

               <Box sx={{ my: 1 }}>
                  <Divider />
               </Box>

               <NavItem
                  label="Settings"
                  icon={<SettingsIcon />}
                  active={isActive(
                     '/admin_profilsetting'
                  )}
                  onClick={() =>
                     navigate(
                        '/admin_profilsetting'
                     )
                  }
               />

               <NavItem
                  label="Invoices"
                  icon={
                     <FormatAlignLeftIcon />
                  }
                  active={isActive(
                     '/admin_invoice'
                  )}
                  onClick={() =>
                     navigate('/admin_invoice')
                  }
               />

               <NavItem
                  label="Support"
                  icon={<ErrorOutlineIcon />}
                  count={unreadCount1}
                  active={isActive(
                     '/admin_support'
                  )}
                  onClick={() =>
                     navigate('/admin_support')
                  }
               />

               <NavItem
                  label="Archive"
                  icon={<ArchiveIcon />}
                  count={unreadCount2}
                  active={isActive(
                     '/admin_archive'
                  )}
                  onClick={() =>
                     navigate('/admin_archive')
                  }
               />
            </List>
         </Drawer>

         <Main open={open}>
            <Box
               sx={{
                  height: `${headerHeight}px`,
                  minHeight: `${headerHeight}px`,
               }}
            />

            <Box
               sx={{
                  display: 'flex',
                  width: '100%',
                  minHeight:
                     `calc(100vh - ${headerHeight}px)`,
               }}
            >
               <Outlet />
            </Box>
         </Main>
      </Box>
   )
}